import { useCallback, useEffect, useState } from 'react';
import { useServices } from '../../app/services';
import { getMeta, setMeta } from '../../data/appMeta';

/**
 * Custom Home (V1.5): which sections Home shows and in what order. The
 * month's total always leads (it is what Home is for); everything under it
 * can be hidden or moved. Kept per device, like the theme.
 */
export const HOME_SECTIONS = ['pace', 'anomalies', 'bills', 'breakdown', 'trend', 'recent'] as const;
export type HomeSection = (typeof HOME_SECTIONS)[number];

export interface HomeSlot {
  key: HomeSection;
  visible: boolean;
}

export const LAYOUT_KEY = 'home.layout';

/** The trend is new with the custom Home: offered, not forced on anyone. */
export const DEFAULT_LAYOUT: readonly HomeSlot[] = HOME_SECTIONS.map((key) => ({ key, visible: key !== 'trend' }));

/**
 * A stored layout, made whole: unknown sections dropped, each section once,
 * and sections added in later versions put where the default has them
 * (right after the section before them there), with their default visibility.
 */
export function parseLayout(raw: string | undefined): HomeSlot[] {
  let list: unknown = [];
  try {
    list = raw ? JSON.parse(raw) : [];
  } catch {
    list = [];
  }
  const out: HomeSlot[] = [];
  if (Array.isArray(list)) {
    for (const x of list) {
      if (!x || typeof x !== 'object') continue;
      const { key, visible } = x as { key?: unknown; visible?: unknown };
      if (!HOME_SECTIONS.includes(key as HomeSection) || out.some((s) => s.key === key)) continue;
      out.push({ key: key as HomeSection, visible: visible !== false });
    }
  }
  if (out.length === 0) return DEFAULT_LAYOUT.map((s) => ({ ...s }));
  DEFAULT_LAYOUT.forEach((slot, i) => {
    if (out.some((s) => s.key === slot.key)) return;
    // Right after the nearest section that comes before it in the default (first if none is there).
    const prev = DEFAULT_LAYOUT.slice(0, i)
      .reverse()
      .find((p) => out.some((s) => s.key === p.key));
    out.splice(prev ? out.findIndex((s) => s.key === prev.key) + 1 : 0, 0, { ...slot });
  });
  return out;
}

export function moveSection(layout: readonly HomeSlot[], key: HomeSection, by: -1 | 1): HomeSlot[] {
  const i = layout.findIndex((s) => s.key === key);
  const j = i + by;
  if (i < 0 || j < 0 || j >= layout.length) return [...layout];
  const next = [...layout];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

export function showSection(layout: readonly HomeSlot[], key: HomeSection, visible: boolean): HomeSlot[] {
  return layout.map((s) => (s.key === key ? { ...s, visible } : s));
}

export const isDefault = (layout: readonly HomeSlot[]) =>
  layout.length === DEFAULT_LAYOUT.length && layout.every((s, i) => s.key === DEFAULT_LAYOUT[i]!.key && s.visible === DEFAULT_LAYOUT[i]!.visible);

/** This device's Home layout (the default until it is read), and a way to change it. */
export function useHomeLayout() {
  const { db } = useServices();
  const [layout, setLayout] = useState<HomeSlot[] | null>(null);
  useEffect(() => {
    let live = true;
    void getMeta(db, LAYOUT_KEY).then((raw) => live && setLayout(parseLayout(raw)));
    return () => {
      live = false;
    };
  }, [db]);
  const change = useCallback(
    (next: HomeSlot[]) => {
      setLayout(next);
      void setMeta(db, LAYOUT_KEY, JSON.stringify(next));
    },
    [db],
  );
  return { layout: layout ?? DEFAULT_LAYOUT.map((s) => ({ ...s })), ready: layout !== null, change };
}
